import { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaCheck } from 'react-icons/fa';

interface AttendeeUser {
  id?: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user?: { id?: string; first_name?: string; last_name?: string; email?: string };
}

interface AttendeeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  users: AttendeeUser[];
  selectedUsers: string[];
  maxUsers: number;
  onConfirm: (selectedUsers: string[]) => void;
}

export const AttendeeSelectionModal = ({
  isOpen,
  onClose,
  title,
  users,
  selectedUsers,
  maxUsers,
  onConfirm
}: AttendeeSelectionModalProps) => {
  const [localSelection, setLocalSelection] = useState<string[]>(selectedUsers);

  useEffect(() => {
    setLocalSelection(selectedUsers);
  }, [selectedUsers, isOpen]);

  if (!isOpen) return null;

  const handleUserToggle = (userId: string) => {
    setLocalSelection(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else if (prev.length < maxUsers) {
        return [...prev, userId];
      }
      return prev;
    });
  };

  const handleConfirm = () => {
    onConfirm(localSelection);
    onClose();
  };

  const getUserName = (user: AttendeeUser) => {
    return `${user.user?.first_name || user.first_name} ${user.user?.last_name || user.last_name}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
          <h3 className="text-lg font-semibold text-[#171717]">{title}</h3>
          <button
            onClick={onClose}
            className="text-[#aaa] hover:text-[#555] transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <p className="text-sm text-[#555]">
              Select up to {maxUsers} attendees ({localSelection.length}/{maxUsers} selected)
            </p>
          </div>

          <div className="space-y-2">
            {users.map((user) => {
              const userId = user.user_id || user.user?.id || user.id || '';
              const isSelected = localSelection.includes(userId);
              const isDisabled = !isSelected && localSelection.length >= maxUsers;
              
              return (
                <div
                  key={userId}
                  onClick={() => !isDisabled && handleUserToggle(userId)}
                  className={`flex items-center p-3 rounded-lg border transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-[#f5f5f5] border-[#eaeaea] ring-1 ring-blue-500'
                      : isDisabled
                      ? 'bg-[#fafafa] border-[#eaeaea] cursor-not-allowed opacity-60'
                      : 'bg-white border-[#eaeaea] hover:bg-[#fafafa]'
                  }`}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    {/* Avatar */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-[#555]' : 'bg-[#eaeaea]'
                    }`}>
                      {isSelected ? (
                        <FaCheck className="w-3 h-3 text-white" />
                      ) : (
                        <FaUser className={`w-3 h-3 ${isSelected ? 'text-white' : 'text-[#888]'}`} />
                      )}
                    </div>

                    {/* User Info */}
                    <div className="flex-1">
                      <div className={`font-medium ${isSelected ? 'text-[#171717]' : 'text-[#171717]'}`}>
                        {getUserName(user)}
                      </div>
                      {user.user?.email && (
                        <div className="text-xs text-[#888]">{user.user.email}</div>
                      )}
                    </div>

                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected
                        ? 'bg-[#555] border-[#888]'
                        : 'border-[#eaeaea]'
                    }`}>
                      {isSelected && <FaCheck className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {users.length === 0 && (
            <div className="text-center py-8 text-[#888]">
              <FaUser className="w-12 h-12 mx-auto mb-4 text-[#ccc]" />
              <p>No users available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[#eaeaea]">
          <div className="text-sm text-[#888]">
            {localSelection.length} of {maxUsers} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#555] bg-[#f5f5f5] rounded-lg hover:bg-[#eaeaea] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-[#171717] rounded-lg hover:bg-[#333] transition-colors"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 